// useStandalone.js
import { useEffect, useState } from "react";

export function useIsStandalone() {
  const [isStandalone, setIsStandalone] = useState(() => {
    if (typeof navigator !== "undefined" && "standalone" in navigator) {
      return Boolean(navigator.standalone); // iOS Safari
    }
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(display-mode: standalone)").matches;
    }
    return false;
  });

  useEffect(() => {
    // listener for matchMedia changes (for modern browsers)
    let mql;
    if (typeof window !== "undefined" && window.matchMedia) {
      mql = window.matchMedia("(display-mode: standalone)");
      const handler = (e) => setIsStandalone(Boolean(e.matches));
      try {
        // old & new APIs
        if (mql.addEventListener) mql.addEventListener("change", handler);
        else if (mql.addListener) mql.addListener(handler);
      } catch (err) {
        // ignore
      }
    }

    // Some browsers (Android/Chrome) fire 'appinstalled' event when installed
    const onAppInstalled = () => setIsStandalone(true);
    window.addEventListener?.("appinstalled", onAppInstalled);

    // iOS: navigator.standalone can't be listened to, but page visibility or pageshow might change
    const onVisibility = () => {
      const val =
        (typeof navigator !== "undefined" && navigator.standalone) ||
        (typeof window !== "undefined" &&
          window.matchMedia &&
          window.matchMedia("(display-mode: standalone)").matches);
      setIsStandalone(Boolean(val));
    };
    window.addEventListener?.("pageshow", onVisibility);
    window.addEventListener?.("visibilitychange", onVisibility);

    return () => {
      if (mql) {
        try {
          if (mql.removeEventListener) mql.removeEventListener("change", () => {});
          else if (mql.removeListener) mql.removeListener(() => {});
        } catch (err) {}
      }
      window.removeEventListener?.("appinstalled", onAppInstalled);
      window.removeEventListener?.("pageshow", onVisibility);
      window.removeEventListener?.("visibilitychange", onVisibility);
    };
  }, []);

  return isStandalone;
}
